import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Save, Edit3, Building2, Users, Filter, X } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { useWorkflow, useUpdateWorkflow } from '@/hooks/useWorkflows';
import { ConditionRules, Workflow, WorkflowUpdateRequest } from '@/types/workflow';
import { WorkflowCreationWizard } from './WorkflowCreationWizard';

// Work item preview component - uses data from workflow API response
const WorkItemsPreview: React.FC<{ 
  workItems: Array<{
    id: string;
    templateId: string;
    templateName: string;
    executionOrder: number;
    isConditional?: boolean;
    conditionRules?: ConditionRules;
  }> 
}> = ({ workItems }) => {
  if (!workItems || workItems.length === 0) {
    return (
      <div className="ml-6 space-y-2">
        <p className="text-sm text-gray-500">此步驟無工作項目</p>
      </div>
    );
  }

  return (
    <div className="ml-6 space-y-2">
      {workItems
        .sort((a, b) => a.executionOrder - b.executionOrder)
        .map((workItem) => (
        <div key={workItem.id} className="p-3 bg-white border rounded-lg shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 flex-1">
              <Badge variant="outline" className="text-xs w-7 h-7 flex items-center justify-center p-0">
                {workItem.executionOrder}
              </Badge>
              <div className="flex-1">
                <div className="text-sm font-medium">{workItem.templateName}</div>
              </div>
            </div>
            
            {/* Conditional Indicator */}
            {workItem.isConditional && (
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1">
                  <Filter className="h-3 w-3 text-blue-600" />
                  <span className="text-xs text-blue-600 font-medium">條件執行</span>
                </div>
                {workItem.conditionRules && workItem.conditionRules.conditions.length > 0 && (
                  <Badge variant="secondary" className="text-xs px-2 py-1">
                    {workItem.conditionRules.conditions.length} 條件
                  </Badge>
                )}
              </div>
            )}
          </div>
          
          {/* Condition Rules Display */}
          {workItem.isConditional && workItem.conditionRules && workItem.conditionRules.conditions.length > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-100">
              <div className="text-xs text-gray-600 mb-1">
                條件規則 ({workItem.conditionRules.type === 'ALL' ? '全部符合' : '任一符合'}):
              </div>
              <div className="space-y-1">
                {workItem.conditionRules.conditions.map((condition, index) => (
                  <div key={index} className="text-xs text-gray-700 bg-gray-50 px-2 py-1 rounded">
                    <span className="font-medium">{condition.field}</span>
                    <span className="mx-1">{getOperatorLabel(condition.operator)}</span>
                    {condition.value !== null && condition.value !== undefined && (
                      <span className="font-medium">
                        {Array.isArray(condition.value) 
                          ? condition.value.join(', ') 
                          : String(condition.value)
                        }
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Helper function to get operator labels
const getOperatorLabel = (operator: string): string => {
  const labels: Record<string, string> = {
    'EQUALS': '等於',
    'NOT_EQUALS': '不等於',
    'IN': '在列表中',
    'NOT_IN': '不在列表中',
    'GREATER_THAN': '大於',
    'LESS_THAN': '小於',
    'CONTAINS': '包含',
    'NOT_CONTAINS': '不包含'
  };
  return labels[operator] || operator;
};

interface WorkflowDetailViewProps {
  workflowId: string;
  onBack: () => void;
}

export const WorkflowDetailView: React.FC<WorkflowDetailViewProps> = ({ workflowId, onBack }) => {
  const [isEditing, setIsEditing] = useState(false);
  const { toast } = useToast();
  
  // Fetch real workflow data
  const { data: workflow, isLoading, error } = useWorkflow(workflowId);
  
  // Update mutation
  const updateWorkflowMutation = useUpdateWorkflow({
    onSuccess: () => {
      setIsEditing(false);
    }
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <p className="text-gray-600">載入工作流程資料中...</p>
        </div>
      </div>
    );
  }

  if (error || !workflow) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            返回工作流程管理
          </Button>
        </div>
        <div className="text-center py-8">
          <p className="text-gray-600">找不到指定的工作流程</p>
        </div>
      </div>
    );
  }

  // Convert workflow data to format expected by creation wizard
  const convertToEditFormat = (workflow: Workflow) => {
    return {
      name: workflow.name,
      description: workflow.description || '',
      workflow_steps: workflow.workflowSteps?.map(step => ({
        id: step.id || '',
        name: step.name,
        description: step.description || '',
        step_number: step.stepNumber,
        workflow_step_departments: step.workflowStepDepartments?.map(dept => ({
          id: dept.id || '',
          department_id: dept.departmentId,
          department_name: dept.departmentName || '',
          execution_order: dept.executionOrder,
          is_required: dept.isRequired || true,
          work_items: dept.workItems?.map(item => ({
            id: item.id || '',
            template_id: item.templateId,
            template_name: item.templateName || '',
            template_description: item.templateDescription || '',
            execution_order: item.executionOrder,
            is_conditional: item.isConditional || false,
            condition_rules: item.conditionRules
          })) || []
        })) || []
      })) || []
    };
  };

  if (isEditing) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={() => setIsEditing(false)}>
            <X className="mr-2 h-4 w-4" />
            取消編輯
          </Button>
          <h2 className="text-xl font-semibold">編輯工作流程: {workflow.name}</h2>
        </div>
        
        <WorkflowCreationWizard 
          editMode={true}
          initialData={convertToEditFormat(workflow)}
          workflowId={workflowId}
          onSuccess={() => setIsEditing(false)}
          onCancel={() => setIsEditing(false)}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 頁面標題和操作 */}
      <div className="space-y-4">
        {/* 返回按鈕 */}
        <div>
          <Button variant="outline" onClick={onBack} className="mb-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            返回工作流程管理
          </Button>
        </div>
        
        {/* 工作流程資訊卡片 */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-3xl font-bold text-gray-900">{workflow.name}</h1>
                  <Badge 
                    variant={workflow.isActive ? "default" : "secondary"}
                    className={`text-sm px-3 py-1 ${
                      workflow.isActive 
                        ? 'bg-green-100 text-green-800 border-green-200' 
                        : 'bg-gray-100 text-gray-600 border-gray-200'
                    }`}
                  >
                    {workflow.isActive ? "✓ 啟用中" : "⏸ 已停用"}
                  </Badge>
                </div>
                
                <div className="flex items-center justify-between mt-3">
                  {workflow.description ? (
                    <p className="text-lg text-gray-600 leading-relaxed">
                      {workflow.description}
                    </p>
                  ) : (
                    <p className="text-lg text-gray-400 italic">
                      暫無描述
                    </p>
                  )}
                  
                  {workflow.createdAt && (
                    <div className="text-sm text-gray-500">
                      {new Date(workflow.createdAt).toLocaleDateString('zh-TW')}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Edit Button */}
              <div className="flex-shrink-0">
                <Button onClick={() => setIsEditing(true)} className="flex items-center gap-2">
                  <Edit3 className="h-4 w-4" />
                  編輯工作流程
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 工作流程結構 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            工作流程結構
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {workflow.workflowSteps?.map((step, stepIndex) => (
              <div key={step.id} className="border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Badge variant="default" className="text-sm font-bold">
                    步驟 {stepIndex + 1}
                  </Badge>
                  <span className="font-medium text-lg">{step.name}</span>
                  {step.description && (
                    <span className="text-sm text-gray-500">- {step.description}</span>
                  )}
                </div>
                
                {/* 並行組視覺化 */}
                {(() => {
                  const parallelGroups = step.workflowStepDepartments?.reduce((groups, dept) => {
                    const group = dept.executionOrder || 1;
                    if (!groups[group]) groups[group] = [];
                    groups[group].push(dept);
                    return groups;
                  }, {} as Record<number, Array<any>>) || {};

                  // Define colors for different parallel groups
                  const groupColors = [
                    'border-blue-500 bg-blue-50',
                    'border-green-500 bg-green-50', 
                    'border-purple-500 bg-purple-50',
                    'border-orange-500 bg-orange-50',
                    'border-pink-500 bg-pink-50',
                    'border-indigo-500 bg-indigo-50'
                  ];

                  return (
                    <div className="ml-6 space-y-4">
                      {Object.entries(parallelGroups)
                        .sort(([a], [b]) => Number(a) - Number(b))
                        .map(([groupNum, departments], index) => (
                          <div 
                            key={groupNum} 
                            className={`border-l-4 pl-4 py-2 rounded-r ${groupColors[index % groupColors.length]}`}
                          >
                            <div className="flex items-center gap-2 mb-3">
                              <Badge variant="outline" className="text-xs border-blue-500 text-blue-700">
                                並行組: {groupNum}
                              </Badge>
                              {departments.length > 1 && (
                                <span className="text-xs text-gray-600">
                                  同組可並行執行
                                </span>
                              )}
                            </div>
                            
                            <div className="grid gap-3">
                              {departments.map(dept => (
                                <div key={dept.id} className="bg-white border rounded-lg p-3">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Building2 className="h-4 w-4 text-gray-500" />
                                    <span className="font-medium text-sm">{dept.departmentName || `部門 ${dept.departmentId}`}</span>
                                  </div>
                                  
                                  <WorkItemsPreview workItems={dept.workItems || []} />
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                    </div>
                  );
                })()}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};